/**
 * Freigabe-Richtlinie ([BR] „Freigabe-Richtlinie“, [MCP] 9.10, [V] E-j; Anhang A.approval).
 *
 * Modi: `require` (Vorschau + Freigabe, Standard), `auto` (sofort, protokolliert), `off` (deaktiviert).
 * Harte Untergrenzen (Einwilligung, Phrasenfilter, Limits, Eigentümer-/Scope-Prüfung …) gelten immer;
 * `auto` ersetzt nur die menschliche Bestätigung. `ApprovalGate.decide` ist der einzige Leseweg.
 *
 * Abweichung von [MCP] 9.10 (R-3): `disallowed_reasons` ist ein `z.partialRecord`, weil `z.record` mit
 * Enum-Schlüsseln in Zod 4 erschöpfend ist (alle drei Modi wären Pflicht).
 */
import { z } from 'zod';
import type { ActionKey } from './actions.js';
import type { DbTx, DomainContext, PersonId, TriggerOrigin } from './common.js';

export const ApprovalMode = z.enum(['require', 'auto', 'off']);
export type ApprovalMode = z.infer<typeof ApprovalMode>;

/** Uhrzeit `HH:MM` (00:00 bis 23:59); strenger als `\d{2}:\d{2}` in [V] 4.3, damit `24:00` oder `12:60` nie durchgehen. */
const HH_MM = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);

export const ApprovalConditions = z.strictObject({
  max_auto_per_day: z.int().min(1).max(100).optional(),
  min_score: z.number().min(0).max(100).optional(),
  audience_ids: z.array(z.uuid()).min(1).max(20).optional(),
  weekdays_only: z.boolean().optional(),
  time_window: z.object({ from: HH_MM, to: HH_MM }).optional(),
  allow_third_party_trigger: z.boolean().optional(),
});

export const ApprovalPolicy = z.object({
  action_key: z.string(),
  mode: ApprovalMode,
  daily_limit: z.int().min(1).max(500).nullable(),
  conditions: ApprovalConditions,
  version: z.int(),
  updated_at: z.iso.datetime(),
  source: z.enum(['seed', 'dashboard', 'reset']),
});
export type ApprovalPolicy = z.infer<typeof ApprovalPolicy>;

export const ApprovalPolicyView = ApprovalPolicy.extend({
  label: z.string(),
  explanation: z.string(),
  category: z.enum(['outbound', 'internal']),
  allowed_modes: z.array(ApprovalMode),
  disallowed_reasons: z.partialRecord(ApprovalMode, z.string()),
  default_mode: ApprovalMode,
  third_party_trigger: z.enum(['allowed', 'configurable', 'never']),
  supported_conditions: z.array(
    z.enum([
      'max_auto_per_day',
      'min_score',
      'audience_ids',
      'weekdays_only',
      'time_window',
      'allow_third_party_trigger',
    ]),
  ),
  registry_daily_cap: z.int().nullable(),
});
export type ApprovalPolicyView = z.infer<typeof ApprovalPolicyView>;

export const ApprovalPolicyPatch = z.strictObject({
  mode: ApprovalMode,
  daily_limit: z.int().min(1).max(500).nullable(),
  conditions: ApprovalConditions,
  expected_version: z.int(),
  confirm_outbound_auto: z.boolean().optional(),
});
export type ApprovalPolicyPatch = z.infer<typeof ApprovalPolicyPatch>;

/** Speicher der Richtlinien; `userId` stammt aus der Sitzung ([MCP] 9.10 unverändert). */
export interface ApprovalPolicyStore {
  get(tx: DbTx, userId: string, actionKey: string): Promise<ApprovalPolicy>;
  list(userId: string): Promise<ApprovalPolicyView[]>;
  update(
    userId: string,
    actionKey: string,
    patch: ApprovalPolicyPatch,
    actor: { userId: string; sessionId: string },
  ): Promise<ApprovalPolicyView>;
  reset(
    userId: string,
    actor: { userId: string; sessionId: string },
  ): Promise<ApprovalPolicyView[]>;
  seed(
    userId: string,
    initial: Record<string, Pick<ApprovalPolicy, 'mode' | 'daily_limit' | 'conditions'>>,
  ): Promise<number>;
}

export type ApprovalRequest = {
  actionKey: ActionKey;
  triggerOrigin: TriggerOrigin;
  subject?: { personIds?: PersonId[]; audienceIds?: string[]; score?: number | null };
};
export type ApprovalDecision = { mode: ApprovalMode; policyVersion: number; downgraded: string[] };

export interface ApprovalGate {
  decide(tx: DbTx, ctx: DomainContext, req: ApprovalRequest): Promise<ApprovalDecision>;
}
